import React, { useEffect, useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import { usePatientTreatmentContext } from './ContextProvider';
import { useAppContext } from '../../../../Services/AppContext';
import MedicalReportsList from '../../../../Components/MedicalReport/MedicalReportsList';
import EducationMaterials from '../../../../Components/EducationMaterials/EducationMaterials';
import RequestPaymentModal from '../../../../Components/RequestPayment/RequestPaymentModal';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import PatientTreatmentPageWrapper from './PatientTreatmentPageWrapper';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import BaseHealthData from '../../../HealthData/Components/BaseHealthData';
import useSms from '../../Hooks/useSms';
import CaseForwarderModal from '../../../Case/Components/CaseForwarder/CaseForwarderModal';
import PatientsQuestionnaires from '../../../Questionnaire/Components/PatientsQuestionnaires/PatientsQuestionnaires';
import MedicalRequestsList from '../../../MedicalRequests/Components/MedicalRequestsList/MedicalRequestsList';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import Discourse from '../../../Discourse/Components/Discourse/Discourse';
import InsuranceReportsList from '../../../Insurance/Component/InsuranceReportsList/InsuranceReportsList';
import TransmiseList from '../../../TransmiseModule/Component/TransmiseList/TransmiseList';
import CaseTakeOverModal from '../../../Case/Components/CaseTakeOver/CaseTakeOverModal';
import PatientTreatmentLaboratories from './PatientTreatmentLaboratories';

interface PatientTreatmentProfileProps {}

const PatientTreatmentTreatment: React.FC<
	PatientTreatmentProfileProps
> = props => {
	const { patientId, caseId, isMyCase } = usePatientTreatmentContext();
	const { loggedUserId, workplaceId, isMou, globalTemplate } = useAppContext();
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [showRequestPayment, setShowRequestPayment] = useState(false);
	const [showCaseForwarder, setShowCaseForwarder] = useState(false);
	const [isCaseTakeOverOpen, setIsCaseTakeOverOpen] = useState(false);
	const [isSmsConfirmationOpen, setIsSmsConfirmationOpen] = useState(false);
	const [smsConfirmationText, setSmsConfirmationText] = useState('');
	const { isAllowed } = useAclContext();
	const { sendSms, getText } = useSms();

	useEffect(() => {
		handleLoadSteps();
	}, []);

	useEffect(() => {
		handleLoadSteps();
	}, [caseId]);

	const handleSendSmsNotification = async (caseId: string) => {
		const result = await sendSms(caseId);
		if (result) {
			const smsText = await getText(caseId);
			if (smsText) {
				setSmsConfirmationText(smsText);
				setIsSmsConfirmationOpen(true);
			}
		}
	};

	const handleLoadSteps = () => {
		const tempSteps: NavigationStepperItemType[] = [
			{
				name: 'patientTreatment.cure.dekurz.title',
				key: 'discourse',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<Discourse
							caseId={caseId}
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			},
		];

		if (isAllowed('patient_health_data')) {
			tempSteps.push({
				name: 'patientTreatment.cure.healthData.title',
				key: 'healthData',
				disabled: isMou,
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<BaseHealthData patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('medical_report')) {
			tempSteps.push({
				name: 'patientTreatment.cure.medicalReports.title',
				key: 'medicalReports',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MedicalReportsList
							patientId={patientId}
							workplaceId={workplaceId}
							loggedUserId={loggedUserId}
							caseId={caseId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('medical_request')) {
			tempSteps.push({
				name: 'patientTreatment.cure.requestForms.title',
				key: 'medicalRequests',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<MedicalRequestsList
							patientId={patientId}
							workplaceId={workplaceId}
							setIsLoading={params.setIsLoading}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('laboratory_requests')) {
			tempSteps.push({
				name: 'patientTreatment.cure.laboratoryRequests.title',
				key: 'laboratoryRequests',
				component: () => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientTreatmentLaboratories
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('questionnaire')) {
			tempSteps.push({
				name: 'patientTreatment.cure.questionnaires.title',
				key: 'questionnaires',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<PatientsQuestionnaires patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('education_material')) {
			tempSteps.push({
				name: 'patientTreatment.cure.educationMaterials.title',
				key: 'educationMaterials',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<EducationMaterials
							patientId={patientId}
							workplaceId={workplaceId}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('insuranceReport')) {
			tempSteps.push({
				name: 'base.insuranceReports',
				key: 'insuranceReports',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<InsuranceReportsList patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (isAllowed('transmise')) {
			tempSteps.push({
				name: 'base.transmission',
				key: 'transmise',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<TransmiseList patientId={patientId} />
					</PatientTreatmentPageWrapper>
				),
			});
		}

		if (caseId) {
			if (isAllowed('notificationUsingSMS')) {
				tempSteps.push({
					name: 'component.sms.sendSMS',
					key: 'notificationUsingSMS',
					onClick: () => handleSendSmsNotification(caseId),
				});
			}

			if (isAllowed('payment_request', 'create')) {
				tempSteps.push({
					name: 'patientTreatment.cure.requestPayment.title',
					key: 'requestPayment',
					onClick: () => setShowRequestPayment(true),
				});
			}

			tempSteps.push({
				name: 'component.caseForwarder.passTheCaseToOtherMedicOrWorkplace',
				key: 'showCaseForwarder',
				onClick: () => setShowCaseForwarder(true),
				// component: () => <CaseForwarder isToggled caseId={caseId} />,
			});
			if (!isMyCase) {
				tempSteps.push({
					name: 'base.takeOverTheCase',
					key: 'takeOverTheCase',
					onClick: () => setIsCaseTakeOverOpen(true),
				});
			}
		}

		setSteps(tempSteps);
	};

	if (!steps.length) {
		return <></>;
	}

	return (
		<>
			<SubTabNavigationStepper
				steps={steps}
				initialStepPath='discourse'
				canSkip={true}
			/>
			{!!caseId && (
				<RequestPaymentModal
					caseId={caseId}
					opened={showRequestPayment}
					onClose={() => setShowRequestPayment(false)}
				/>
			)}
			{!!caseId && showCaseForwarder && (
				<CaseForwarderModal
					opened={showCaseForwarder}
					caseId={caseId}
					onClose={() => setShowCaseForwarder(false)}
				/>
			)}
			{!!caseId && isCaseTakeOverOpen && (
				<CaseTakeOverModal
					isOpen
					caseId={caseId}
					onClose={() => setIsCaseTakeOverOpen(false)}
				/>
			)}
			{!!caseId && isSmsConfirmationOpen && (
				<ConfirmationModal
					title='component.sms.smsSentSuccessfully'
					text={smsConfirmationText}
					isOpen={isSmsConfirmationOpen}
					onClose={() => setIsSmsConfirmationOpen(false)}>
					<Button
						theme={Theme.GREEN}
						title='base.shared.ok'
						onClick={() => setIsSmsConfirmationOpen(false)}
					/>
				</ConfirmationModal>
			)}
		</>
	);
};

export default PatientTreatmentTreatment;
